import React, { useCallback, useEffect, useMemo, useState } from 'react';
import '../Admin.scss';
import './AdminStatistics.scss';
import MainURL from '../../MainURL';
import axios from 'axios';
import { Line, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title as ChartTitle,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  ChartTitle,
  Tooltip,
  Legend,
  Filler,
);

type VisitStatByIp = {
  date: string;
  uniqueVisitors: number;
  totalVisits: number;
};

type VisitStatByCount = {
  date: string;
  totalVisits: number;
};

type CountMetrics = Record<string, VisitStatByCount[]>;

type RetentionRow = {
  date: string;
  newUsers: number;
  retainedUsers: number;
};

type PlatformBreakdownRow = {
  platform: string;
  deviceCount: number;
};

type VersionBreakdownRow = {
  appVersion: string;
  deviceCount: number;
};

type PlatformStats = {
  visitors: VisitStatByIp[];
  metrics: CountMetrics;
  retention: RetentionRow[];
};

type AppPlatformStats = PlatformStats & {
  platformBreakdown: PlatformBreakdownRow[];
  versionBreakdown: VersionBreakdownRow[];
};

type DashboardResponse = {
  web: PlatformStats;
  app: AppPlatformStats;
  users: UserStats;
};

type UserStats = {
  totalUsers: number;
  todaySignups: number;
};

type MergedStatRow = {
  date: string;
  uniqueVisitors: number;
  totalVisits: number;
  mainConnect: number;
  recruitView: number;
  retreatMenu: number;
  serviceMenu: number;
  praiseView: number;
  aiServiceMenu: number;
  ministerInfoView: number;
  ministerInfoDetail: number;
};

const METRIC_KEYS = [
  'mainconnect',
  'recruitview',
  'retreatmenu',
  'servicemenu',
  'praisewordclick',
  'aiservicemenu',
  'ministerinfoview',
  'ministerinfodetail',
] as const;

const METRIC_COLUMNS: { key: (typeof METRIC_KEYS)[number]; label: string }[] = [
  { key: 'mainconnect', label: '메인 접속수' },
  { key: 'recruitview', label: 'recruit 접속수' },
  { key: 'retreatmenu', label: '수련회 클릭수' },
  { key: 'servicemenu', label: '서비스 클릭수' },
  { key: 'praisewordclick', label: '찬양 클릭수' },
  { key: 'aiservicemenu', label: 'AI설교지원 접속수' },
  { key: 'ministerinfoview', label: '사역자정보 접속수' },
  { key: 'ministerinfodetail', label: '사역자정보 상세 클릭수' },
];

/** 랭킹/차트에서 공통으로 쓰는 메뉴 지표 메타데이터 (라벨/색상/조회 함수) */
const RANK_METRICS: { label: string; color: string; accessor: (row: MergedStatRow) => number }[] = [
  { label: '메인 접속수', color: '#2563EB', accessor: (r) => r.mainConnect },
  { label: 'Recruit 접속수', color: '#06B6D4', accessor: (r) => r.recruitView },
  { label: '수련회 클릭수', color: '#8B5CF6', accessor: (r) => r.retreatMenu },
  { label: '서비스 클릭수', color: '#94A3B8', accessor: (r) => r.serviceMenu },
  { label: '찬양 클릭수', color: '#F59E0B', accessor: (r) => r.praiseView },
  { label: 'AI설교지원 접속수', color: '#EC4899', accessor: (r) => r.aiServiceMenu },
  { label: '사역자정보 접속수', color: '#10B981', accessor: (r) => r.ministerInfoView },
  { label: '사역자정보 상세 클릭수', color: '#F97316', accessor: (r) => r.ministerInfoDetail },
];

/** API/DB에서 오는 ISO 타임스탬프·혼합 포맷을 YYYY-MM-DD로 통일 */
function normalizeStatDate(value: unknown): string {
  const raw = String(value ?? '').trim();
  const match = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : raw;
}

function formatNumber(value: number): string {
  return Math.round(value).toLocaleString('ko-KR');
}

function formatShortDate(date: string): string {
  const parts = normalizeStatDate(date).split('-');
  return parts.length === 3 ? `${parts[1]}.${parts[2]}` : date;
}

function mergePlatformStats(platform: PlatformStats): MergedStatRow[] {
  const allDates = new Set<string>();
  platform.visitors.forEach((row) => allDates.add(normalizeStatDate(row.date)));
  METRIC_KEYS.forEach((key) => {
    (platform.metrics[key] || []).forEach((row) => allDates.add(normalizeStatDate(row.date)));
  });

  // 기기 시계가 빠른 앱에서 들어온 미래 날짜는 제외 (맨 위 행이 '오늘'로 쓰임)
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

  return Array.from(allDates)
    .filter((date) => date <= today)
    .sort((a, b) => b.localeCompare(a))
    .map((date) => {
      const ipStat = platform.visitors.find((s) => normalizeStatDate(s.date) === date);
      const metricValue = (key: (typeof METRIC_KEYS)[number]) =>
        Number(platform.metrics[key]?.find((s) => normalizeStatDate(s.date) === date)?.totalVisits ?? 0);

      return {
        date,
        uniqueVisitors: Number(ipStat?.uniqueVisitors ?? 0),
        totalVisits: Number(ipStat?.totalVisits ?? 0),
        mainConnect: metricValue('mainconnect'),
        recruitView: metricValue('recruitview'),
        retreatMenu: metricValue('retreatmenu'),
        serviceMenu: metricValue('servicemenu'),
        praiseView: metricValue('praisewordclick'),
        aiServiceMenu: metricValue('aiservicemenu'),
        ministerInfoView: metricValue('ministerinfoview'),
        ministerInfoDetail: metricValue('ministerinfodetail'),
      };
    });
}

type StatsTab = 'web' | 'app';

const STATS_TABS: { value: StatsTab; label: string }[] = [
  { value: 'web', label: '웹 통계' },
  { value: 'app', label: '앱 통계' },
];

const PLATFORM_HINTS: Record<StatsTab, string> = {
  web: '홈페이지(client) 접속·메뉴 클릭 데이터입니다. 순방문자는 IP 기준입니다.',
  app: 'React Native 앱(comministermore) 데이터입니다. 순방문자는 기기 ID 기준이며, 앱 실행·탭·화면 이벤트는 app_ 접두사로 저장됩니다.',
};

/** 두 값의 증감을 ▲/▼ 배지로 표시 */
function DeltaBadge({ current, previous }: { current: number; previous: number }) {
  const diff = current - previous;
  if (diff === 0) {
    return <span className="admin-stats-delta is-flat">— 동일</span>;
  }
  const pct = previous === 0 ? 100 : Math.round((diff / previous) * 100);
  const up = diff > 0;
  return (
    <span className={`admin-stats-delta ${up ? 'is-up' : 'is-down'}`}>
      {up ? '▲' : '▼'} {Math.abs(pct)}%
    </span>
  );
}

/** 최근 N개 값을 정규화해 그리는 미니 추세선(스파크라인) */
function Sparkline({ values, color }: { values: number[]; color: string }) {
  const width = 84;
  const height = 26;
  if (values.length < 2) {
    return <svg width={width} height={height} className="admin-stats-spark" />;
  }
  const max = Math.max(...values);
  const min = Math.min(...values);
  const range = max - min || 1;
  const points = values
    .map((v, i) => {
      const x = (i / (values.length - 1)) * width;
      const y = height - ((v - min) / range) * (height - 4) - 2;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="admin-stats-spark">
      <polyline fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" points={points} />
    </svg>
  );
}

function KpiCard({
  label,
  icon,
  iconTone,
  value,
  unit,
  sub,
  current,
  previous,
  sparkValues,
  sparkColor,
}: {
  label: string;
  icon: string;
  iconTone: 'b1' | 'b2' | 'b3' | 'b4';
  value: string;
  unit?: string;
  sub?: string;
  current?: number;
  previous?: number;
  sparkValues?: number[];
  sparkColor?: string;
}) {
  return (
    <div className="admin-stats-kpi">
      <div className="admin-stats-kpi__head">
        <span className="admin-stats-kpi__label">{label}</span>
        <div className={`admin-stats-kpi__icon admin-stats-kpi__icon--${iconTone}`}>{icon}</div>
      </div>
      <div className="admin-stats-kpi__value">
        {value}
        {unit && <small>{unit}</small>}
      </div>
      <div className="admin-stats-kpi__foot">
        {current !== undefined && previous !== undefined ? (
          <DeltaBadge current={current} previous={previous} />
        ) : (
          <span className="admin-stats-kpi__sub">{sub}</span>
        )}
        {sparkValues && sparkValues.length > 1 && (
          <Sparkline values={sparkValues} color={sparkColor ?? '#2563EB'} />
        )}
      </div>
    </div>
  );
}

function UserStatsHero({
  users,
  latestDate,
  webToday,
  appToday,
  updatedAt,
}: {
  users: UserStats;
  latestDate: string | null;
  webToday: MergedStatRow | undefined;
  appToday: MergedStatRow | undefined;
  updatedAt: Date | null;
}) {
  return (
    <div className="admin-stats-hero">
      <div className="admin-stats-hero__main">
        <div className="admin-stats-hero__eyebrow">
          <span className="admin-stats-hero__pulse" /> LIVE · 통합 서비스 현황
        </div>
        <div className="admin-stats-hero__title">
          웹 · 앱 데이터를 한눈에, <span>사역자모아</span> 운영 대시보드
        </div>
        <div className="admin-stats-hero__desc">
          최근 30일간의 방문자·회원·메뉴 이용 데이터를 기준으로 집계됩니다.
        </div>
        <div className="admin-stats-hero__meta">
          <div className="admin-stats-hero__m">
            <span className="admin-stats-hero__lbl">현재 회원수</span>
            <span className="admin-stats-hero__val">{formatNumber(users.totalUsers)}명</span>
          </div>
          <div className="admin-stats-hero__m">
            <span className="admin-stats-hero__lbl">오늘 신규가입</span>
            <span className="admin-stats-hero__val">{formatNumber(users.todaySignups)}명</span>
          </div>
          <div className="admin-stats-hero__m">
            <span className="admin-stats-hero__lbl">최신 집계일</span>
            <span className="admin-stats-hero__val">{latestDate ? normalizeStatDate(latestDate) : '데이터 없음'}</span>
          </div>
          <div className="admin-stats-hero__m">
            <span className="admin-stats-hero__lbl">마지막 갱신</span>
            <span className="admin-stats-hero__val">
              {updatedAt ? updatedAt.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' }) : '-'}
            </span>
          </div>
        </div>
      </div>
      <div className="admin-stats-hero__side">
        <div>
          <div className="admin-stats-hero__side-eyebrow">최신 집계일 스냅샷</div>
          <h3>웹과 앱, 오늘은 어떻게<br />이용되고 있을까요?</h3>
        </div>
        <div className="admin-stats-hero__stats">
          <div className="admin-stats-hero__stat">
            <div className="v">{formatNumber(webToday?.uniqueVisitors ?? 0)}</div>
            <div className="l">웹 순방문자</div>
          </div>
          <div className="admin-stats-hero__stat">
            <div className="v">{formatNumber(appToday?.uniqueVisitors ?? 0)}</div>
            <div className="l">앱 순방문자</div>
          </div>
          <div className="admin-stats-hero__stat">
            <div className="v">{formatNumber((webToday?.totalVisits ?? 0) + (appToday?.totalVisits ?? 0))}</div>
            <div className="l">웹+앱 총 방문</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function MetricRankingCard({ rows, platformLabel }: { rows: MergedStatRow[]; platformLabel: string }) {
  const [range, setRange] = useState<7 | 30>(7);
  const slice = rows.slice(0, range);
  const ranked = useMemo(() => {
    const sums = RANK_METRICS.map((m) => ({
      label: m.label,
      color: m.color,
      value: slice.reduce((acc, row) => acc + m.accessor(row), 0),
    }));
    return sums.sort((a, b) => b.value - a.value);
  }, [slice]);
  const max = Math.max(...ranked.map((r) => r.value), 1);

  return (
    <div className="admin-stats-card">
      <div className="admin-stats-card__head">
        <div>
          <div className="admin-stats-card__title">{platformLabel} 메뉴별 클릭 순위</div>
          <div className="admin-stats-card__sub">누적 클릭수 기준</div>
        </div>
        <div className="admin-stats-chips">
          <button
            type="button"
            className={`admin-stats-chip${range === 7 ? ' is-active' : ''}`}
            onClick={() => setRange(7)}
          >
            7일
          </button>
          <button
            type="button"
            className={`admin-stats-chip${range === 30 ? ' is-active' : ''}`}
            onClick={() => setRange(30)}
          >
            30일
          </button>
        </div>
      </div>
      {rows.length === 0 ? (
        <div className="admin-stats-empty">표시할 데이터가 없습니다.</div>
      ) : (
        <div className="admin-stats-bars">
          {ranked.map((item) => (
            <div className="admin-stats-bar-row" key={item.label}>
              <span className="admin-stats-bar-row__name">{item.label}</span>
              <span className="admin-stats-bar-row__track">
                <span
                  className="admin-stats-bar-row__fill"
                  style={{
                    width: `${Math.max((item.value / max) * 100, item.value > 0 ? 4 : 0)}%`,
                    background: `linear-gradient(90deg, ${item.color}, ${item.color}CC)`,
                  }}
                />
              </span>
              <span className="admin-stats-bar-row__val">{formatNumber(item.value)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DauTrendCard({ rows, platformLabel }: { rows: MergedStatRow[]; platformLabel: string }) {
  const ordered = useMemo(() => [...rows].reverse(), [rows]);
  const labels = ordered.map((r) => formatShortDate(r.date));

  return (
    <div className="admin-stats-card">
      <div className="admin-stats-card__head">
        <div>
          <div className="admin-stats-card__title">{platformLabel} 일별 순방문자 추이</div>
          <div className="admin-stats-card__sub">최근 30일 · 순방문자 / 총 방문수</div>
        </div>
      </div>
      {rows.length === 0 ? (
        <div className="admin-stats-empty">표시할 데이터가 없습니다.</div>
      ) : (
        <div className="admin-stats-linechart">
          <Line
            data={{
              labels,
              datasets: [
                {
                  label: '순방문자수',
                  data: ordered.map((s) => s.uniqueVisitors),
                  borderColor: '#2563EB',
                  backgroundColor: 'rgba(37, 99, 235, 0.14)',
                  tension: 0.35,
                  fill: true,
                  pointRadius: 0,
                  pointHoverRadius: 4,
                },
                {
                  label: '총 방문수',
                  data: ordered.map((s) => s.totalVisits),
                  borderColor: '#06B6D4',
                  backgroundColor: 'rgba(6, 182, 212, 0.10)',
                  tension: 0.35,
                  fill: true,
                  pointRadius: 0,
                  pointHoverRadius: 4,
                },
              ],
            }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              interaction: { mode: 'index', intersect: false },
              plugins: {
                legend: { position: 'top' as const, labels: { boxWidth: 10, font: { size: 11 } } },
                title: { display: false },
              },
              scales: {
                x: { grid: { display: false }, ticks: { font: { size: 10 } } },
                y: { beginAtZero: true, grid: { color: '#F1F5F9' }, ticks: { font: { size: 10 } } },
              },
            }}
          />
        </div>
      )}
    </div>
  );
}

function PlatformSplitCard({ webRows, appRows }: { webRows: MergedStatRow[]; appRows: MergedStatRow[] }) {
  const webTotal = webRows.reduce((acc, r) => acc + r.totalVisits, 0);
  const appTotal = appRows.reduce((acc, r) => acc + r.totalVisits, 0);
  const grandTotal = webTotal + appTotal;
  const webPct = grandTotal > 0 ? Math.round((webTotal / grandTotal) * 100) : 0;
  const appPct = grandTotal > 0 ? 100 - webPct : 0;

  return (
    <div className="admin-stats-card">
      <div className="admin-stats-card__head">
        <div>
          <div className="admin-stats-card__title">웹 · 앱 방문 비중</div>
          <div className="admin-stats-card__sub">최근 30일 총 방문수 기준</div>
        </div>
      </div>
      {grandTotal === 0 ? (
        <div className="admin-stats-empty">표시할 데이터가 없습니다.</div>
      ) : (
        <div className="admin-stats-donut-row">
          <div className="admin-stats-donut">
            <Doughnut
              data={{
                labels: ['웹', '앱'],
                datasets: [
                  {
                    data: [webTotal, appTotal],
                    backgroundColor: ['#2563EB', '#06B6D4'],
                    borderWidth: 0,
                  },
                ],
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                cutout: '72%',
                plugins: { legend: { display: false }, tooltip: { enabled: true } },
              }}
            />
            <div className="admin-stats-donut__center">
              <div className="v">{webPct}%</div>
              <div className="l">웹</div>
            </div>
          </div>
          <div className="admin-stats-legend">
            <div className="admin-stats-legend__item">
              <span className="admin-stats-legend__dot" style={{ background: '#2563EB' }} />
              <span className="admin-stats-legend__name">웹</span>
              <span className="admin-stats-legend__val">{formatNumber(webTotal)}</span>
              <span className="admin-stats-legend__pct">{webPct}%</span>
            </div>
            <div className="admin-stats-legend__item">
              <span className="admin-stats-legend__dot" style={{ background: '#06B6D4' }} />
              <span className="admin-stats-legend__name">앱</span>
              <span className="admin-stats-legend__val">{formatNumber(appTotal)}</span>
              <span className="admin-stats-legend__pct">{appPct}%</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function RecentVisitsListCard({ rows, platformLabel }: { rows: MergedStatRow[]; platformLabel: string }) {
  const recent = rows.slice(0, 5);
  const tones = ['#2563EB', '#06B6D4', '#D97706', '#16A34A', '#8B5CF6'];

  return (
    <div className="admin-stats-card">
      <div className="admin-stats-card__head">
        <div>
          <div className="admin-stats-card__title">{platformLabel} 최근 방문 요약</div>
          <div className="admin-stats-card__sub">최근 5일 · 전일 대비 증감</div>
        </div>
      </div>
      {recent.length === 0 ? (
        <div className="admin-stats-empty">표시할 데이터가 없습니다.</div>
      ) : (
        <div className="admin-stats-list">
          {recent.map((row, i) => {
            const prev = rows[i + 1];
            return (
              <div className="admin-stats-list-row" key={row.date}>
                <div className="admin-stats-list-row__ic" style={{ background: `${tones[i % tones.length]}1A`, color: tones[i % tones.length] }}>
                  {formatShortDate(row.date)}
                </div>
                <div className="admin-stats-list-row__meta">
                  <strong>{normalizeStatDate(row.date)}</strong>
                  <span>총 방문 {formatNumber(row.totalVisits)}건</span>
                </div>
                <div className="admin-stats-list-row__nums">
                  <strong>{formatNumber(row.uniqueVisitors)}명</strong>
                  {prev ? (
                    <DeltaBadge current={row.uniqueVisitors} previous={prev.uniqueVisitors} />
                  ) : (
                    <span className="admin-stats-kpi__sub">신규 집계</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const PLATFORM_LABELS: Record<string, string> = { ios: 'iOS', android: 'Android', unknown: '미확인' };
const PLATFORM_COLORS: Record<string, string> = { ios: '#06B6D4', android: '#2563EB', unknown: '#94A3B8' };

function AppPlatformDonutCard({ rows }: { rows: PlatformBreakdownRow[] }) {
  const sorted = useMemo(() => [...rows].sort((a, b) => b.deviceCount - a.deviceCount), [rows]);
  const total = sorted.reduce((acc, r) => acc + r.deviceCount, 0);
  const top = sorted[0];

  return (
    <div className="admin-stats-card">
      <div className="admin-stats-card__head">
        <div>
          <div className="admin-stats-card__title">앱 플랫폼 구성</div>
          <div className="admin-stats-card__sub">최근 30일 · 기기 수 기준</div>
        </div>
      </div>
      {total === 0 ? (
        <div className="admin-stats-empty">
          아직 수집된 기기 정보가 없습니다. 최신 버전으로 업데이트한 사용자부터 집계됩니다.
        </div>
      ) : (
        <div className="admin-stats-donut-row">
          <div className="admin-stats-donut">
            <Doughnut
              data={{
                labels: sorted.map((r) => PLATFORM_LABELS[r.platform] ?? r.platform),
                datasets: [
                  {
                    data: sorted.map((r) => r.deviceCount),
                    backgroundColor: sorted.map((r) => PLATFORM_COLORS[r.platform] ?? '#94A3B8'),
                    borderWidth: 0,
                  },
                ],
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                cutout: '72%',
                plugins: { legend: { display: false }, tooltip: { enabled: true } },
              }}
            />
            <div className="admin-stats-donut__center">
              <div className="v">{top ? Math.round((top.deviceCount / total) * 100) : 0}%</div>
              <div className="l">{top ? PLATFORM_LABELS[top.platform] ?? top.platform : '-'}</div>
            </div>
          </div>
          <div className="admin-stats-legend">
            {sorted.map((r) => (
              <div className="admin-stats-legend__item" key={r.platform}>
                <span className="admin-stats-legend__dot" style={{ background: PLATFORM_COLORS[r.platform] ?? '#94A3B8' }} />
                <span className="admin-stats-legend__name">{PLATFORM_LABELS[r.platform] ?? r.platform}</span>
                <span className="admin-stats-legend__val">{formatNumber(r.deviceCount)}</span>
                <span className="admin-stats-legend__pct">{total > 0 ? Math.round((r.deviceCount / total) * 100) : 0}%</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function AppVersionListCard({ rows }: { rows: VersionBreakdownRow[] }) {
  const sorted = useMemo(() => [...rows].sort((a, b) => b.deviceCount - a.deviceCount).slice(0, 6), [rows]);
  const total = sorted.reduce((acc, r) => acc + r.deviceCount, 0);

  return (
    <div className="admin-stats-card">
      <div className="admin-stats-card__head">
        <div>
          <div className="admin-stats-card__title">앱 버전 분포</div>
          <div className="admin-stats-card__sub">최근 30일 · 기기 수 기준</div>
        </div>
      </div>
      {sorted.length === 0 ? (
        <div className="admin-stats-empty">아직 수집된 버전 정보가 없습니다.</div>
      ) : (
        <div className="admin-stats-list">
          {sorted.map((r, i) => {
            const isUnknown = r.appVersion === 'unknown';
            return (
              <div className="admin-stats-list-row" key={r.appVersion}>
                <div
                  className="admin-stats-list-row__ic"
                  style={{ background: i === 0 ? '#EFF6FF' : '#F1F5F9', color: i === 0 ? '#2563EB' : '#64748B' }}
                >
                  {isUnknown ? '?' : r.appVersion}
                </div>
                <div className="admin-stats-list-row__meta">
                  <strong>{isUnknown ? '버전 미확인' : `v${r.appVersion}`}</strong>
                  <span>{i === 0 ? '최다 사용 버전' : '사용 중'}</span>
                </div>
                <div className="admin-stats-list-row__nums">
                  <strong>{formatNumber(r.deviceCount)}대</strong>
                  <span>{total > 0 ? Math.round((r.deviceCount / total) * 100) : 0}%</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function RetentionListCard({ rows, platformLabel }: { rows: RetentionRow[]; platformLabel: string }) {
  const recent = (rows ?? []).slice(0, 5);
  const tones = ['#16A34A', '#2563EB', '#D97706', '#8B5CF6', '#06B6D4'];

  return (
    <div className="admin-stats-card">
      <div className="admin-stats-card__head">
        <div>
          <div className="admin-stats-card__title">{platformLabel} 일별 재방문 (D+1)</div>
          <div className="admin-stats-card__sub">신규 방문자 중 다음날 재방문한 비율</div>
        </div>
      </div>
      {recent.length === 0 ? (
        <div className="admin-stats-empty">아직 집계된 재방문 데이터가 없습니다.</div>
      ) : (
        <div className="admin-stats-list">
          {recent.map((row, i) => {
            const pct = row.newUsers > 0 ? Math.round((row.retainedUsers / row.newUsers) * 100) : 0;
            const tone = tones[i % tones.length];
            return (
              <div className="admin-stats-list-row" key={row.date}>
                <div className="admin-stats-list-row__ic" style={{ background: `${tone}1A`, color: tone }}>
                  {formatShortDate(row.date)}
                </div>
                <div className="admin-stats-list-row__meta">
                  <strong>{normalizeStatDate(row.date)} 신규 방문자</strong>
                  <span>{formatNumber(row.newUsers)}명</span>
                </div>
                <div className="admin-stats-list-row__nums">
                  <strong>{formatNumber(row.retainedUsers)}명</strong>
                  <span style={{ color: tone }}>재방문 {pct}%</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function MetricTrendCard({ rows, platformLabel }: { rows: MergedStatRow[]; platformLabel: string }) {
  const ordered = useMemo(() => [...rows].reverse(), [rows]);
  const labels = ordered.map((r) => formatShortDate(r.date));

  return (
    <div className="admin-stats-card">
      <div className="admin-stats-card__head">
        <div>
          <div className="admin-stats-card__title">{platformLabel} 메뉴별 클릭 추이</div>
          <div className="admin-stats-card__sub">최근 30일 · 항목별 클릭수</div>
        </div>
      </div>
      {rows.length === 0 ? (
        <div className="admin-stats-empty">표시할 데이터가 없습니다.</div>
      ) : (
        <div className="admin-stats-linechart admin-stats-linechart--tall">
          <Line
            data={{
              labels,
              datasets: RANK_METRICS.map((m) => ({
                label: m.label,
                data: ordered.map((s) => m.accessor(s)),
                borderColor: m.color,
                backgroundColor: `${m.color}33`,
                tension: 0.3,
                pointRadius: 0,
                pointHoverRadius: 3,
              })),
            }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              interaction: { mode: 'index', intersect: false },
              plugins: {
                legend: { position: 'top' as const, labels: { boxWidth: 10, font: { size: 11 } } },
              },
              scales: {
                x: { grid: { display: false }, ticks: { font: { size: 10 } } },
                y: { beginAtZero: true, grid: { color: '#F1F5F9' }, ticks: { font: { size: 10 } } },
              },
            }}
          />
        </div>
      )}
    </div>
  );
}

function RawDataTable({ rows }: { rows: MergedStatRow[] }) {
  const [expanded, setExpanded] = useState(false);
  const visibleRows = expanded ? rows : rows.slice(0, 7);

  return (
    <div className="admin-stats-card admin-stats-card--table">
      <div className="admin-stats-card__head">
        <div>
          <div className="admin-stats-card__title">일자별 원본 데이터</div>
          <div className="admin-stats-card__sub">최근 30일 · 표 형태 전체 지표</div>
        </div>
        {rows.length > 7 && (
          <button type="button" className="admin-stats-chip" onClick={() => setExpanded((v) => !v)}>
            {expanded ? '접기' : `전체 ${rows.length}일 보기`}
          </button>
        )}
      </div>
      {rows.length === 0 ? (
        <div className="admin-stats-empty">표시할 데이터가 없습니다.</div>
      ) : (
        <div className="admin-stats-table-scroll">
          <table className="admin-stats-table">
            <thead>
              <tr>
                <th>날짜</th>
                <th>순방문자수</th>
                <th>총 방문수</th>
                {METRIC_COLUMNS.map((col) => (
                  <th key={col.key}>{col.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((stat) => (
                <tr key={stat.date}>
                  <td>{normalizeStatDate(stat.date)}</td>
                  <td>{stat.uniqueVisitors}</td>
                  <td>{stat.totalVisits}</td>
                  <td>{stat.mainConnect}</td>
                  <td>{stat.recruitView}</td>
                  <td>{stat.retreatMenu}</td>
                  <td>{stat.serviceMenu}</td>
                  <td>{stat.praiseView}</td>
                  <td>{stat.aiServiceMenu}</td>
                  <td>{stat.ministerInfoView}</td>
                  <td>{stat.ministerInfoDetail}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function PlatformSection({
  label,
  hint,
  rows,
  retention,
  isApp,
  platformBreakdown,
  versionBreakdown,
}: {
  label: string;
  hint: string;
  rows: MergedStatRow[];
  retention: RetentionRow[];
  isApp?: boolean;
  platformBreakdown?: PlatformBreakdownRow[];
  versionBreakdown?: VersionBreakdownRow[];
}) {
  const latestRow = rows[0];
  const prevRow = rows[1];
  const last7 = useMemo(() => [...rows.slice(0, 7)].reverse(), [rows]);
  const top7Metric = useMemo(() => {
    const slice = rows.slice(0, 7);
    const sums = RANK_METRICS.map((m) => ({
      label: m.label,
      value: slice.reduce((acc, row) => acc + m.accessor(row), 0),
    }));
    sums.sort((a, b) => b.value - a.value);
    return sums[0];
  }, [rows]);

  return (
    <section className="admin-stats-platform-section">
      <div className="admin-stats-platform-section__head">
        <p className="admin-stats-platform-section__hint">{hint}</p>
      </div>

      <div className="admin-stats-kpi-row admin-stats-kpi-row--mini">
        <KpiCard
          label={`${label} 순방문자수`}
          icon="◉"
          iconTone="b2"
          value={formatNumber(latestRow?.uniqueVisitors ?? 0)}
          unit="명"
          current={latestRow?.uniqueVisitors ?? 0}
          previous={prevRow?.uniqueVisitors ?? 0}
          sparkValues={last7.map((r) => r.uniqueVisitors)}
          sparkColor="#2563EB"
        />
        <KpiCard
          label={`${label} 최근 7일 인기 메뉴`}
          icon="★"
          iconTone="b4"
          value={top7Metric ? formatNumber(top7Metric.value) : '0'}
          unit={top7Metric ? `회 · ${top7Metric.label}` : undefined}
          sub="7일 누적 클릭 기준"
        />
      </div>

      <div className="admin-stats-grid-2">
        <MetricRankingCard rows={rows} platformLabel={label} />
        <DauTrendCard rows={rows} platformLabel={label} />
      </div>

      <div className="admin-stats-grid-2">
        <RecentVisitsListCard rows={rows} platformLabel={label} />
        <RetentionListCard rows={retention} platformLabel={label} />
      </div>

      {isApp && (
        <div className="admin-stats-grid-2">
          <AppPlatformDonutCard rows={platformBreakdown ?? []} />
          <AppVersionListCard rows={versionBreakdown ?? []} />
        </div>
      )}

      <div className="admin-stats-grid-1">
        <MetricTrendCard rows={rows} platformLabel={label} />
      </div>

      <div className="admin-stats-grid-1">
        <RawDataTable rows={rows} />
      </div>
    </section>
  );
}

/** 재방문/기기수 지표는 백엔드가 문자열(BIGINT)로 내려줄 수 있어, 합산 전에 숫자로 강제 변환 */
function normalizeRetention(rows: RetentionRow[] | undefined): RetentionRow[] {
  return (rows ?? []).map((r) => ({
    date: r.date,
    newUsers: Number(r.newUsers ?? 0),
    retainedUsers: Number(r.retainedUsers ?? 0),
  }));
}

function normalizeDeviceCount<T extends { deviceCount: unknown }>(rows: T[] | undefined): T[] {
  return (rows ?? []).map((r) => ({ ...r, deviceCount: Number(r.deviceCount ?? 0) }));
}

const EMPTY_WEB_STATS: PlatformStats = { visitors: [], metrics: {}, retention: [] };
const EMPTY_APP_STATS: AppPlatformStats = {
  visitors: [],
  metrics: {},
  retention: [],
  platformBreakdown: [],
  versionBreakdown: [],
};

export default function AdminStatistics() {
  const [activeTab, setActiveTab] = useState<StatsTab>('web');
  const [webStats, setWebStats] = useState<PlatformStats>(EMPTY_WEB_STATS);
  const [appStats, setAppStats] = useState<AppPlatformStats>(EMPTY_APP_STATS);
  const [userStats, setUserStats] = useState<UserStats>({ totalUsers: 0, todaySignups: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);

  const fetchVisitStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get<DashboardResponse>(`${MainURL}/admin/statistics/dashboard`);
      setWebStats({
        ...EMPTY_WEB_STATS,
        ...res.data?.web,
        retention: normalizeRetention(res.data?.web?.retention),
      });
      setAppStats({
        ...EMPTY_APP_STATS,
        ...res.data?.app,
        retention: normalizeRetention(res.data?.app?.retention),
        platformBreakdown: normalizeDeviceCount(res.data?.app?.platformBreakdown),
        versionBreakdown: normalizeDeviceCount(res.data?.app?.versionBreakdown),
      });
      setUserStats({
        totalUsers: Number(res.data?.users?.totalUsers ?? 0),
        todaySignups: Number(res.data?.users?.todaySignups ?? 0),
      });
      setUpdatedAt(new Date());
    } catch {
      setError('통계 데이터를 불러오지 못했습니다.');
      setWebStats(EMPTY_WEB_STATS);
      setAppStats(EMPTY_APP_STATS);
      setUserStats({ totalUsers: 0, todaySignups: 0 });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchVisitStats();
  }, [fetchVisitStats]);

  const webRows = useMemo(() => mergePlatformStats(webStats), [webStats]);
  const appRows = useMemo(() => mergePlatformStats(appStats), [appStats]);
  const latestDate = webRows[0]?.date ?? appRows[0]?.date ?? null;

  return (
    <div className="admin-register admin-stats-dash">
      <div className="inner">
        <div className="admin-stats-topbar">
          <div>
            <h2 className="admin-stats-page-title">방문자 · 서비스 통계</h2>
            <p className="admin-stats-page-sub">웹(client)과 앱(comministermore)의 접속·이용 데이터를 통합해 보여줍니다.</p>
          </div>
          <button type="button" className="admin-stats-refresh" onClick={() => void fetchVisitStats()} disabled={loading}>
            {loading ? '불러오는 중...' : '새로고침'}
          </button>
        </div>

        {!loading && !error && (
          <UserStatsHero
            users={userStats}
            latestDate={latestDate}
            webToday={webRows[0]}
            appToday={appRows[0]}
            updatedAt={updatedAt}
          />
        )}

        {loading ? (
          <div className="admin-stats-empty admin-stats-empty--page">로딩중...</div>
        ) : error ? (
          <div className="admin-stats-empty admin-stats-empty--page admin-stats-empty--error">{error}</div>
        ) : (
          <>
            <div className="admin-stats-kpi-row">
              <KpiCard
                label="현재 회원수"
                icon="👥"
                iconTone="b1"
                value={formatNumber(userStats.totalUsers)}
                unit="명"
                sub="누적 가입자"
              />
              <KpiCard
                label="오늘 신규가입"
                icon="✚"
                iconTone="b3"
                value={formatNumber(userStats.todaySignups)}
                unit="명"
                sub="당일 기준"
              />
            </div>

            <div className="admin-stats-grid-1">
              <PlatformSplitCard webRows={webRows} appRows={appRows} />
            </div>

            <div className="admin-stats-tabs" role="tablist" aria-label="통계 플랫폼">
              {STATS_TABS.map(({ value, label }) => (
                <button
                  key={value}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === value}
                  className={`admin-stats-tab${activeTab === value ? ' is-active' : ''}`}
                  onClick={() => setActiveTab(value)}
                >
                  {label}
                </button>
              ))}
            </div>

            {activeTab === 'web' ? (
              <PlatformSection label="웹 통계" hint={PLATFORM_HINTS.web} rows={webRows} retention={webStats.retention} />
            ) : (
              <PlatformSection
                label="앱 통계"
                hint={PLATFORM_HINTS.app}
                rows={appRows}
                retention={appStats.retention}
                isApp
                platformBreakdown={appStats.platformBreakdown}
                versionBreakdown={appStats.versionBreakdown}
              />
            )}
          </>
        )}
      </div>
      <div style={{ height: '200px' }} />
    </div>
  );
}
